// Presentation helpers shared by the Storm Center widgets. Pure: no React,
// no network, so they are unit-tested directly.

const COMPASS = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];

export function compass(deg) {
  const n = Number(deg);
  if (!Number.isFinite(n)) return "";
  return COMPASS[Math.round((((n % 360) + 360) % 360) / 22.5) % 16];
}

export const THREAT = {
  tornado: { label: "TORNADO", color: "#d946ef", rank: 4 },
  severe: { label: "SEVERE", color: "#ef4444", rank: 3 },
  strong: { label: "STRONG", color: "#f97316", rank: 2 },
  moderate: { label: "MOD", color: "#22c55e", rank: 1 },
};
export const threatOf = (t) => THREAT[t] || THREAT.moderate;

// NWS's own map colors for the warnings an EM desk watches, so a board
// matches what the weather service and TV stations show.
const ALERT_STYLE = {
  "Tornado Warning": { color: "#ff0000", rank: 100 },
  "Extreme Wind Warning": { color: "#ff8c00", rank: 95 },
  "Severe Thunderstorm Warning": { color: "#ffa500", rank: 90 },
  "Flash Flood Warning": { color: "#8b0000", rank: 85 },
  "Snow Squall Warning": { color: "#c71585", rank: 80 },
  "Hurricane Warning": { color: "#dc143c", rank: 78 },
  "Storm Surge Warning": { color: "#b524f7", rank: 77 },
  "Tropical Storm Warning": { color: "#b22222", rank: 76 },
  "Tornado Watch": { color: "#ffff00", rank: 70 },
  "Severe Thunderstorm Watch": { color: "#db7093", rank: 65 },
  "Special Marine Warning": { color: "#ffa500", rank: 60 },
  "Blizzard Warning": { color: "#ff4500", rank: 55 },
  "Ice Storm Warning": { color: "#8b008b", rank: 54 },
  "Winter Storm Warning": { color: "#ff69b4", rank: 53 },
  "Flood Warning": { color: "#00ff00", rank: 50 },
  "Special Weather Statement": { color: "#ffe4b5", rank: 20 },
};
export function alertStyle(event) {
  return ALERT_STYLE[event] || { color: "#94a3b8", rank: 0 };
}
export function sortAlerts(alerts = []) {
  return [...alerts].sort(
    (a, b) =>
      (b.affectsStation ? 1 : 0) - (a.affectsStation ? 1 : 0) ||
      alertStyle(b.event).rank - alertStyle(a.event).rank ||
      (a.expiresMs || 0) - (b.expiresMs || 0)
  );
}

// Buckets a free-text report type ("Tstm Wnd Gst", "Hail", "Funnel Cloud",
// mPING's "Hail 1.00 inch"...) into the handful of symbols the map draws.
export function reportKind(type = "") {
  const t = String(type).toLowerCase();
  if (/tornado|funnel|wall cloud/.test(t)) return "tornado";
  if (/hail/.test(t)) return "hail";
  if (/flood|water/.test(t)) return "flood";
  if (/wind|gust|tstm|damage|tree|power line|wires/.test(t)) return "wind";
  if (/snow|sleet|ice|freezing/.test(t)) return "winter";
  return "other";
}
export const REPORT_STYLE = {
  tornado: { color: "#d946ef", glyph: "T" },
  hail: { color: "#22d3ee", glyph: "H" },
  wind: { color: "#60a5fa", glyph: "W" },
  flood: { color: "#34d399", glyph: "F" },
  winter: { color: "#e2e8f0", glyph: "S" },
  other: { color: "#facc15", glyph: "•" },
};

export function reportMagnitude(r) {
  if (r.magnitude == null || r.magnitude === "") return "";
  const unit = String(r.unit || "").toLowerCase();
  const kind = reportKind(r.type);
  if (kind === "hail" || unit === "inch" || unit === "in") return `${r.magnitude}″`;
  if (kind === "wind" || /mph|kt|knot/.test(unit)) return `${r.magnitude} ${unit.startsWith("k") ? "kt" : "mph"}`;
  return `${r.magnitude}${unit ? ` ${unit}` : ""}`;
}

export function filterReports(reports = [], { hours = 3, sources = "all", now = Date.now() } = {}) {
  const since = now - Number(hours || 3) * 3600 * 1000;
  return reports.filter(
    (r) => (r.timeMs || 0) >= since && (sources === "all" || !sources || r.source === sources)
  );
}

export function sortCells(cells = [], { approachingFirst = true } = {}) {
  return [...cells].sort((a, b) => {
    if (approachingFirst) {
      const aa = a.approaching ? 1 : 0;
      const bb = b.approaching ? 1 : 0;
      if (aa !== bb) return bb - aa;
      if (aa && bb && (a.etaMin ?? 1e9) !== (b.etaMin ?? 1e9)) return (a.etaMin ?? 1e9) - (b.etaMin ?? 1e9);
    }
    return threatOf(b.threat).rank - threatOf(a.threat).rank || (a.distanceMi ?? 1e9) - (b.distanceMi ?? 1e9);
  });
}

export function relTime(ms, now = Date.now()) {
  if (!ms) return "";
  const min = Math.round((now - ms) / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  return `${h}h ${min % 60}m ago`;
}

export function clockTime(ms) {
  if (!ms) return "";
  return new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

// Polygon approximating a circle of radiusMi around a point, for range rings.
export function circlePolygon(lat, lng, radiusMi, steps = 96) {
  const R = 3958.7613;
  const d = radiusMi / R;
  const φ1 = (lat * Math.PI) / 180;
  const λ1 = (lng * Math.PI) / 180;
  const ring = [];
  for (let i = 0; i <= steps; i += 1) {
    const θ = (2 * Math.PI * i) / steps;
    const φ2 = Math.asin(Math.sin(φ1) * Math.cos(d) + Math.cos(φ1) * Math.sin(d) * Math.cos(θ));
    const λ2 = λ1 + Math.atan2(Math.sin(θ) * Math.sin(d) * Math.cos(φ1), Math.cos(d) - Math.sin(φ1) * Math.sin(φ2));
    ring.push([(((λ2 * 180) / Math.PI + 540) % 360) - 180, (φ2 * 180) / Math.PI]);
  }
  return { type: "Polygon", coordinates: [ring] };
}

// IEM's NEXRAD base-reflectivity mosaic, as 5-minute frames going back 50
// minutes. `bucket` changes every 5 minutes so browsers and Mapbox fetch fresh
// frames instead of serving a cached, older image under the same URL.
export const RADAR_TILE_TEMPLATE =
  "https://mesonet.agron.iastate.edu/cache/tile.py/1.0.0/nexrad-n0q-900913{frame}/{z}/{x}/{y}.png";
export function radarFrames({ loop = true, now = Date.now() } = {}) {
  const bucket = Math.floor(now / (5 * 60 * 1000));
  const minutes = loop ? [50, 45, 40, 35, 30, 25, 20, 15, 10, 5, 0] : [0];
  return minutes.map((m) => {
    const frame = m === 0 ? "" : `-m${String(m).padStart(2, "0")}m`;
    return {
      id: `radar-${bucket}-${m}`,
      minutesAgo: m,
      tiles: [`${RADAR_TILE_TEMPLATE.replace("{frame}", frame)}?v=${bucket}`],
    };
  });
}
